import CheckBoxIcon from '@mui/icons-material/CheckBox';
import WarningIcon from '@mui/icons-material/Warning';
import { Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { enginePhysicalData } from '../conversion/engine';
import { geometryPhysicalData } from '../conversion/geometry';
import { physicalData, thickness } from '../conversion/physicalData';
import { isOkMinimumRevetmentThickness, isOkMinimumRevetmentWidthBProtection, isOkUnderKeelClearance, resultPhysicalData } from '../conversion/result';
import { scenarioPhysicalData } from '../conversion/scenario';
import { EPropellerDirectionWithRespectToTheQuay, ETypeOfPropeller, ETypeOfQuay, EUnitOfMeasure } from '../shared/enum';
import { IPhysicalData, TInputEngine, TInputGeometry, TInputScenario } from '../shared/iProject';
import variable from '../shared/style/variable';
import { EReduxState } from '../store/enum';
import { useAppSelector } from '../store/hook';
import { selectProject } from '../store/project/projectSlice';
import { selectResult } from '../store/result/resultSlice';
import { getNameScenario } from './MultiScenario';
import i18n from '../localization';

interface ICommonResultProps {}

export const getTypeOfQuayLabel = (typeOfQuay: ETypeOfQuay) => {
    switch (typeOfQuay) {
        case ETypeOfQuay.Vertical:
            return i18n.t('vertical');
        case ETypeOfQuay.Sloped:
            return i18n.t('sloped');
        case ETypeOfQuay.SlopedWithPiles:
            return i18n.t('slopedWithPiles');
    }
};

export const getTypeOfPropellerLabel = (typeOfPropeller: ETypeOfPropeller) => {
    switch (typeOfPropeller) {
        case ETypeOfPropeller.MainPropeller:
            return i18n.t('mainPropeller');
        case ETypeOfPropeller.BowThruster:
            return i18n.t('bowThruster');
    }
};

export const getPropellerDirectionWithRespectToTheQuayLabel = (propellerDirectionWithRespectToTheQuay: EPropellerDirectionWithRespectToTheQuay) => {
    switch (propellerDirectionWithRespectToTheQuay) {
        case EPropellerDirectionWithRespectToTheQuay.Parallel:
            return i18n.t('parallel');
        case EPropellerDirectionWithRespectToTheQuay.Transversal:
            return i18n.t('transversal');
    }
};

const CommonResult = (props: ICommonResultProps) => {
    const project = useAppSelector(selectProject);
    const result = useAppSelector(selectResult);

    const { t } = useTranslation();

    const inputGeometry = {
        typeOfQuay: getTypeOfQuayLabel(project.data!.geometry!.typeOfQuay),
        quaySlope: project.data!.geometry!.quaySlope,
        pileDiameter: project.data!.geometry!.pileDiameter,
        distanceBetweenPileAxis: project.data!.geometry!.distanceBetweenPileAxis,
        minimumWaterDepth: project.data!.geometry!.minimumWaterDepth,
        designWaterLevel: project.data!.geometry!.designWaterLevel,
        designDredgingLevel: project.data!.geometry!.designDredgingLevel,
        minimumUnderKeelClerance: project.data!.geometry!.minimumUnderKeelClerance,
        distanceBetweenVesselAndQuay: project.data!.geometry!.distanceBetweenVesselAndQuay,
    };

    const inputMaterial = {
        thickness: project.data?.doubleLayerMaterial
            ? project.data?.project?.unitOfMeasure == EUnitOfMeasure.Metric
                ? `${project.data?.material?.physicalData[0].thickness} + ${project.data?.doubleLayerMaterial?.physicalData[0].thickness}`
                : `${project.data?.material?.physicalData[0].thicknessIn} + ${project.data?.doubleLayerMaterial?.physicalData[0].thicknessIn}`
            : project.data?.project?.unitOfMeasure == EUnitOfMeasure.Metric
            ? project.data?.material?.physicalData[0].thickness
            : project.data?.material?.physicalData[0].thicknessIn,
        rockD50: project.data?.project?.unitOfMeasure == EUnitOfMeasure.Metric ? project.data?.material?.physicalData[0].rockD50 : project.data?.material?.physicalData[0].rockD50in,
        equivalentThickness:
            project.data?.project?.unitOfMeasure == EUnitOfMeasure.Metric ? project.data?.material?.physicalData[0].equivalentThickness : project.data?.material?.physicalData[0].equivalentThicknessIn,
        shieldsParameter: project.data?.material?.physicalData[0].shieldsParameter,
        relativeDensity: project.data?.material?.physicalData[0].relativeDensity,
        geotextileFilter: project.data?.material?.geotextileFilter,
        xties: project.data?.material?.xties,
        doubleLayerAtTransition: project.data?.doubleLayerMaterial ? !!project.data?.doubleLayerMaterial : null,
        material: project.data?.doubleLayerMaterial ? project.data!.doubleLayerMaterial.translations![0].name : null,
        doubleMaterialThickness: project.data?.doubleLayerMaterial
            ? project.data?.project?.unitOfMeasure == EUnitOfMeasure.Metric
                ? project.data?.doubleLayerMaterial?.physicalData[0].thickness
                : project.data?.doubleLayerMaterial?.physicalData[0].thicknessIn
            : null,
    };

    const inputScenario = {
        vesselType: project.data!.scenarios!.map(t => t.vesselType),
        typeOfPropeller: project.data!.engines!.map(t => getTypeOfPropellerLabel(t.typeOfPropeller)),
        propellerDirectionWithRespectToTheQuay: project.data!.engines!.map(t => getPropellerDirectionWithRespectToTheQuayLabel(t.propellerDirectionWithRespectToTheQuay)),
        numberOfPropellers: project.data!.engines!.map(t => t.numberOfPropellers),
        draft: project.data!.scenarios!.map(t => t.draft),
        vesselBeamBs: project.data!.scenarios!.map(t => t.vesselBeamBs),
        distanceBetweenPropellerAxes: project.data!.engines!.map(t => t.distanceBetweenPropellerAxes),
        propellerDiameter: project.data!.engines!.map(t => t.propellerDiameter),
        propellerEquippedWithNozzles: project.data!.scenarios!.map(y => y.propellerEquippedWithNozzles ? t('yes') :  t('no')),
        maximumPowerOfThePropeller: project.data!.engines!.map(t => t.maximumPowerOfThePropeller),
        percentageOfUseInPortManeuvers: project.data!.engines!.map(t => t.percentageOfUseInPortManeuvers),
        effectivePowerUsedByThePropeller: project.data!.scenarios!.map(t => t.effectivePowerUsedByThePropeller),
        coefficentC3: project.data!.scenarios!.map(t => t.coefficentC3),
        verticalDistanceBetweenTheAxisOfThePropellerAndTheKell: project.data!.scenarios!.map(t => t.verticalDistanceBetweenTheAxisOfThePropellerAndTheKell),
        propellerDistanceFromQuayL: project.data!.scenarios!.map(t => t.propellerDistanceFromQuayL),
        horizontalDistanceBetweenPropellerAxisAndTheSlopeToe: project.data!.scenarios!.map(t => t.horizontalDistanceBetweenPropellerAxisAndTheSlopeToe),
    };

    return (
        <>
            {result.status == EReduxState.fulfilled && (
                <Stack variant='scrollable' style={{ overflowX: 'auto' }}>
                    <Stack minWidth={900}>
                        <Typography variant='subtitle2' paddingBottom={'0.5rem'}>
                            {t('summaryInput')}
                        </Typography>
                        <Stack direction={'row'} spacing={2}>
                            <Stack width={'50%'}>
                                <TableContainer sx={{ padding: '2rem 3rem' }}>
                                    <Table aria-label='geometry-table'>
                                        <TableHead variant='green'>
                                            <TableRow>
                                                <TableCell>{t('geometry')}</TableCell>
                                                <TableCell>{t('value')}</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {Object.entries(inputGeometry).map((x, u) => (
                                                <TableRow key={x[0]} variant={u % 2 === 0 ? 'greyLight' : 'white'}>
                                                    <TableCell component='th' scope='row'>
                                                        {t(x[0], {
                                                            unitOfMeasure: geometryPhysicalData[x[0] as TInputGeometry].unitOfMeasure[project.data!.project!.unitOfMeasure!],
                                                        })}
                                                    </TableCell>
                                                    <TableCell align='right'>{x[1]}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Stack>
                            <Stack width={'50%'}>
                                <TableContainer sx={{ padding: '2rem 3rem' }}>
                                    <Table aria-label='Material-table'>
                                        <TableHead variant='green'>
                                            <TableRow>
                                                <TableCell>
                                                    <Stack direction={'row'}>
                                                        <Stack>{t('material') + '-'}</Stack>
                                                        <Stack fontWeight={600}>{project.data?.material?.translations![0].name}</Stack>
                                                    </Stack>
                                                </TableCell>
                                                <TableCell>{t('value')}</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {Object.entries(inputMaterial)
                                                .filter(x => x[1])
                                                .map((x, u) => (
                                                    <TableRow key={x[0]} variant={u % 2 === 0 ? 'greyLight' : 'white'}>
                                                        <TableCell component='th' scope='row' style={{ fontWeight: x[0] == thickness ? 600 : '' }}>
                                                            {t(
                                                                x[0],
                                                                physicalData[x[0] as IPhysicalData] && {
                                                                    unitOfMeasure: physicalData[x[0] as IPhysicalData].unitOfMeasure[project.data!.project!.unitOfMeasure!],
                                                                },
                                                            )}
                                                        </TableCell>
                                                        <TableCell align='right'>{typeof x[1] == 'boolean' ? (x[1] ? t('yes') : t('no')) : x[1]}</TableCell>
                                                    </TableRow>
                                                ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Stack>
                        </Stack>
                        <Typography variant='subtitle2' paddingBottom={'0.5rem'}>
                            {t('validation')}
                        </Typography>
                        <TableContainer sx={{ padding: '2rem 3rem' }}>
                            <Table aria-label='validation-table'>
                                <TableHead variant='green'>
                                    <TableRow>
                                        <TableCell>{t('scenarios')}</TableCell>
                                        <TableCell colSpan={3}>
                                            {t('minimumRevetmentThickness', { unitOfMeasure: resultPhysicalData['minimumRevetmentThickness']?.unitOfMeasure[project.data!.project!.unitOfMeasure!] })}
                                        </TableCell>
                                        <TableCell colSpan={3}>
                                            {t('minimumRevetmentWidthBProtection', {
                                                unitOfMeasure: resultPhysicalData['minimumRevetmentWidthBProtection']?.unitOfMeasure[project.data!.project!.unitOfMeasure!],
                                            })}
                                        </TableCell>
                                        <TableCell colSpan={3}>
                                            {t('minimumUnderKeelClerance', { unitOfMeasure: geometryPhysicalData['minimumUnderKeelClerance']?.unitOfMeasure[project.data!.project!.unitOfMeasure!] })}
                                        </TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell></TableCell>
                                        {[0, 1, 2].map(x => (
                                            <>
                                                <TableCell>{t('value')}</TableCell>
                                                <TableCell>{t('calculated')}</TableCell>
                                                <TableCell>{t('check')}</TableCell>
                                            </>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {project.data?.scenarios?.map((x, i) => {
                                        let obj = [
                                            result.data!.openPiledQuayScenarios?.[i],
                                            result.data!.propellerAxisParallelToTheQuayStructureScenarios?.[i],
                                            result.data!.propellerAxisTransverseToSlopedQuayStructureScenarios?.[i],
                                            result.data!.propellerAxisTransverseToVerticalQuayStructureScenarios?.[i],
                                        ]
                                            .filter(x => x !== undefined)
                                            .find(y => Object.values(y).some(x => x !== null && x !== false));

                                        let isOkMinimumRevetmentWidthBProtection = true;
                                        if (
                                            result.data!.propellerAxisParallelToTheQuayStructureScenarios &&
                                            Object.values(result.data!.propellerAxisParallelToTheQuayStructureScenarios[i]!).some(x => x !== null && x !== false)
                                        ) {
                                            isOkMinimumRevetmentWidthBProtection = result.data!.propellerAxisParallelToTheQuayStructureScenarios[i].isOkMinimumRevetmentWidthBProtection!;
                                        }

                                        let isOk = obj!.isOkMinimumRevetmentThickness! && isOkMinimumRevetmentWidthBProtection && result.data!.flowVelocityAtThePropellers![i].isOkUnderKeelClearance!;

                                        let thickness = project.data?.doubleLayerMaterial
                                            ? project.data?.project?.unitOfMeasure == EUnitOfMeasure.Metric
                                                ? `${project.data.doubleLayerMaterial.physicalData[0].thickness} + ${project.data?.material?.physicalData[0].thickness!}`
                                                : `${project.data.doubleLayerMaterial.physicalData[0].thicknessIn} + ${project.data?.material?.physicalData[0].thicknessIn!}`
                                            : project.data?.project?.unitOfMeasure == EUnitOfMeasure.Metric
                                            ? project.data?.material?.physicalData[0].thickness
                                            : project.data?.material?.physicalData[0].thicknessIn;

                                        return (
                                            <TableRow key={x.id} style={{ border: isOk ? '' : `3px solid ${variable.red}` }} variant={i % 2 === 0 ? 'greyLight' : 'white'}>
                                                <TableCell style={{ color: isOk ? '' : variable.red }} component='th' scope='row'>
                                                    {getNameScenario(x, project.data!.engines![i])}
                                                </TableCell>
                                                <TableCell style={{ color: obj?.isOkMinimumRevetmentThickness ? '' : variable.red }} component='th' scope='row'>
                                                    {thickness}
                                                </TableCell>
                                                <TableCell style={{ color: obj?.isOkMinimumRevetmentThickness ? '' : variable.red }} component='th' scope='row'>
                                                    {obj!.minimumRevetmentThickness}
                                                </TableCell>
                                                <TableCell style={{ textAlign: 'center' }} component='th' scope='row'>
                                                    {obj?.isOkMinimumRevetmentThickness ? <CheckBoxIcon style={{ fill: variable.green }} /> : <WarningIcon style={{ fill: variable.yellow }} />}
                                                </TableCell>

                                                <TableCell
                                                    style={{
                                                        color: isOkMinimumRevetmentWidthBProtection ? '' : variable.red,
                                                    }}
                                                    component='th'
                                                    scope='row'>
                                                    {project.data!.material?.physicalData[0].widthOfTheProtectionW ?? '-'}
                                                </TableCell>
                                                <TableCell
                                                    style={{
                                                        color: isOkMinimumRevetmentWidthBProtection ? '' : variable.red,
                                                    }}
                                                    component='th'
                                                    scope='row'>
                                                    {result.data!.propellerAxisParallelToTheQuayStructureScenarios?.[i].minimumRevetmentWidthBProtection ?? '-'}
                                                </TableCell>
                                                <TableCell
                                                    style={{
                                                        textAlign: 'center',
                                                    }}
                                                    component='th'
                                                    scope='row'>
                                                    {result.data!.propellerAxisParallelToTheQuayStructureScenarios?.[i].minimumRevetmentWidthBProtection ? (
                                                        isOkMinimumRevetmentWidthBProtection ? (
                                                            <CheckBoxIcon style={{ fill: variable.green }} />
                                                        ) : (
                                                            <WarningIcon style={{ fill: variable.yellow }} />
                                                        )
                                                    ) : (
                                                        '-'
                                                    )}
                                                </TableCell>

                                                <TableCell style={{ color: result.data!.flowVelocityAtThePropellers![i].isOkUnderKeelClearance ? '' : variable.red }} component='th' scope='row'>
                                                    {project.data!.geometry?.minimumUnderKeelClerance}
                                                </TableCell>
                                                <TableCell style={{ color: result.data!.flowVelocityAtThePropellers![i].isOkUnderKeelClearance ? '' : variable.red }} component='th' scope='row'>
                                                    {result.data!.flowVelocityAtThePropellers![i].underKeelClearance}
                                                </TableCell>
                                                <TableCell style={{ textAlign: 'center' }} component='th' scope='row'>
                                                    {result.data!.flowVelocityAtThePropellers![i].isOkUnderKeelClearance ? (
                                                        <CheckBoxIcon style={{ fill: variable.green }} />
                                                    ) : (
                                                        <WarningIcon style={{ fill: variable.yellow }} />
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <Typography variant='subtitle2' paddingBottom={'0.5rem'}>
                            {t('inputOutputScenarios')}
                        </Typography>
                        <TableContainer sx={{ padding: '2rem 3rem' }}>
                            <Table aria-label='input-scenarios-table'>
                                <TableHead variant='green'>
                                    <TableRow>
                                        <TableCell>{t('input')}</TableCell>
                                        {project.data?.scenarios?.map((x, i) => (
                                            <TableCell align='right'>{getNameScenario(x, project.data!.engines![i])}</TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {Object.entries(inputScenario).map((x, u) => (
                                        <TableRow key={x[0]} variant={u % 2 === 0 ? 'greyLight' : 'white'}>
                                            <TableCell component='th' scope='row'>
                                                {t(x[0], {
                                                    unitOfMeasure: (scenarioPhysicalData[x[0] as TInputScenario] ?? enginePhysicalData[x[0] as TInputEngine]).unitOfMeasure[
                                                        project.data!.project!.unitOfMeasure!
                                                    ],
                                                })}
                                            </TableCell>
                                            {x[1].map(t => (
                                                <TableCell align='right'>{t ?? '-'}</TableCell>
                                            ))}
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        {Object.keys(result.data!).map(x => (
                            <TableContainer sx={{ padding: '2rem 3rem' }}>
                                <Table aria-label='output-scenarios-table'>
                                    <TableHead variant='green'>
                                        <TableRow>
                                            <TableCell>{t(x)}</TableCell>
                                            {project.data?.scenarios?.map((x, i) => (
                                                <TableCell align='right'>{getNameScenario(x, project.data!.engines![i])}</TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {Object.entries(result.data![x as TResult]![0])
                                            .filter(i => typeof i[1] !== 'boolean')
                                            .map((y, u) => (
                                                <TableRow key={y[0]} variant={u % 2 === 0 ? 'greyLight' : 'white'}>
                                                    <TableCell component='th' scope='row'>
                                                        {t(y[0], {
                                                            unitOfMeasure: resultPhysicalData[y[0] as TResultPhysicalData].unitOfMeasure[project.data!.project!.unitOfMeasure!],
                                                        })}
                                                    </TableCell>
                                                    {project.data?.scenarios?.map((z, i) => (
                                                        <TableCell align='right'>{result.data![x as TResult]![i][y[0]] ?? '-'}</TableCell>
                                                    ))}
                                                </TableRow>
                                            ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        ))}
                    </Stack>
                </Stack>
            )}
        </>
    );
};

export default CommonResult;
